import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type MiamiFamilyOrthodontistLocalization = {
	heroTitle: string;
	introHeading: string;
	doctorHeading: string;
	doctorParagraphs: string[];
	careHeading: string;
	careBeforePractice: string;
	practiceLink: string;
	careBeforeTreatment: string;
	treatmentLink: string;
	careBeforeInvisalign: string;
	invisalignLink: string;
	careBeforeAges: string;
	agesLink: string;
	careAfterAges: string;
	childrenHeading: string;
	childrenParagraphs: string[];
	teensHeading: string;
	teensParagraphs: string[];
	teenCeramicBefore: string;
	teenCeramicLink: string;
	teenCeramicMiddle: string;
	teenInvisalignLink: string;
	teenCeramicAfter: string;
	adultsHeading: string;
	adultParagraphs: string[];
	finalBeforePractice: string;
	finalMiddle: string;
	finalBeforeConsult: string;
	finalConsultLink: string;
	finalAfterConsult: string;
};

const miamiFamilyOrthodontistLocalization = {
	[Locale.EN]: {
		heroTitle: "Miami Family Orthodontist",
		introHeading: "Meet Your City Family Orthodontist",
		doctorHeading: "Meet Dr. Garcia-Paul",
		doctorParagraphs: [
			"Dr. Carmen Garcia-Paul was born and raised in Miami and—despite leaving the area for graduate studies—has always considered the area her home.",
			"She graduated from Our Lady of Lourdes Academy where she was both a scholar-athlete and valedictorian of her class. After Lourdes, Dr. Garcia-Paul was accepted into the honors program at the University of Miami.",
			"While an undergraduate, Dr. Garcia-Paul began working for several local dentists and dental specialists in Miami. It was during this time that she first became interested in the specialty of orthodontics.",
			"After seeing the positive effect that orthodontics could have on a person’s self-esteem, personality, and confidence, she decided to dedicate her life to helping others by perfecting their smiles.",
		],
		careHeading: "Care For All Ages From Your Miami Family Orthodontist",
		careBeforePractice: "At ",
		practiceLink: "Miami Orthodontic Specialists",
		careBeforeTreatment:
			", we believe that everyone should be able to access quality orthodontic ",
		treatmentLink: "treatment",
		careBeforeInvisalign:
			" and that everyone deserves a beautifully aligned smile. We are proud to offer a full range of orthodontic services, including braces and ",
		invisalignLink: "Invisalign",
		careBeforeAges: ", for ",
		agesLink: "children, teens, and adults",
		careAfterAges: " at our office in Miami.",
		childrenHeading: "Miami Orthodontics for Children",
		childrenParagraphs: [
			"In order to ensure that your child is as healthy as possible, it's important that their teeth and jaws are properly aligned, giving them a healthy bite. Many parents are surprised to learn that the American Association of Orthodontists recommends that children get an orthodontic check-up no later than age 7.",
			"By that age, your child's teeth have developed enough so that small problems with jaw growth and emerging teeth can be spotted while they still have some baby teeth. A check-up may reveal that your child's bite is fine and that braces won't be necessary, or we may identify a potential problem and recommended that we monitor it. From there, we may begin treatment at a time that is appropriate for the child. In other cases, we may recommended early treatment.",
		],
		teensHeading: "Orthodontic Care for Miami Teens",
		teensParagraphs: [
			"Early treatment allows our team to monitor the growth and development of your child's jaw, meaning we can catch and begin treating problems while they're still in the early stages. This can cut down the length of treatment considerably.",
			"Teens often want to know, “How long will I be in braces?” There's no set answer, as each case is different, just like each smile is unique. We're always going to do everything that we can to ensure that your teen has their most beautiful, forever smile. Fortunately, today's braces are a big improvement over the ones that previous generations have experienced. They're smaller, sleeker, and far more comfortable than ever before. They also come in a variety of styles, materials and colors.",
		],
		teenCeramicBefore:
			"In addition, teens also have more treatment options than ever before. In addition to traditional metal braces, teens also have the option of choosing clear, ",
		teenCeramicLink: "ceramic braces",
		teenCeramicMiddle:
			", which work the same way traditional braces do, but are designed to blend in with your teen's teeth, giving a more subtle appearance. Also, if your teen wants a new smile with no wires, whatsoever, they might consider treatment using clear aligners, such as Invisalign. ",
		teenInvisalignLink: "Invisalign",
		teenCeramicAfter:
			" straighten teeth gradually using a series of clear, removable trays that fit over the teeth.",
		adultsHeading: "Adult Orthodontics in Miami",
		adultParagraphs: [
			"Orthodontic treatment isn't just for children and teens. More adults than ever are seeking beautiful, forever smiles through orthodontic treatment. Today, roughly 1-in-5 orthodontic patients are adults, so it's never too late to get that stunning smile that you've always wanted.",
			"Many adults are choosing to receive treatment later in life because they understand how important it is to maintain their oral health, they want to feel better about their overall appearance, or both. We also see many adults who have received orthodontic treatment during their early years, but are in need of a “touch up” to regain the smiles they once had.",
		],
		finalBeforePractice: "At ",
		finalMiddle:
			", we recognize that children, teens, and adults will all have different dental needs and need different levels of attention and care. We work diligently with each patient to ensure you receive the most appropriate treatment for you in the most inviting environment.",
		finalBeforeConsult: "Schedule a ",
		finalConsultLink: "free consultation",
		finalAfterConsult: " with us today to get started.",
	},
	[Locale.ES]: {
		heroTitle: "Ortodoncista familiar en Miami",
		introHeading: "Conoce a tu ortodoncista familiar en Miami",
		doctorHeading: "Conoce a la Dra. Garcia-Paul",
		doctorParagraphs: [
			"La Dra. Carmen Garcia-Paul nació y creció en Miami y, aunque salió del área para realizar estudios de posgrado, siempre ha considerado esta zona su hogar.",
			"Se graduó de Our Lady of Lourdes Academy, donde fue atleta académica y valedictorian de su clase. Después de Lourdes, la Dra. Garcia-Paul fue aceptada en el programa de honores de la University of Miami.",
			"Mientras era estudiante universitaria, la Dra. Garcia-Paul comenzó a trabajar con varios dentistas y especialistas dentales locales en Miami. Fue durante este tiempo cuando se interesó por primera vez en la especialidad de ortodoncia.",
			"Después de ver el efecto positivo que la ortodoncia podía tener en la autoestima, personalidad y confianza de una persona, decidió dedicar su vida a ayudar a los demás perfeccionando sus sonrisas.",
		],
		careHeading:
			"Atención para todas las edades de tu ortodoncista familiar en Miami",
		careBeforePractice: "En ",
		practiceLink: "Miami Orthodontic Specialists",
		careBeforeTreatment: ", creemos que todos deben poder acceder a un ",
		treatmentLink: "tratamiento",
		careBeforeInvisalign:
			" de ortodoncia de calidad y que todos merecen una sonrisa bien alineada. Nos enorgullece ofrecer una gama completa de servicios de ortodoncia, incluidos brackets e ",
		invisalignLink: "Invisalign",
		careBeforeAges: ", para ",
		agesLink: "niños, adolescentes y adultos",
		careAfterAges: " en nuestra oficina de Miami.",
		childrenHeading: "Ortodoncia en Miami para niños",
		childrenParagraphs: [
			"Para asegurar que tu hijo esté lo más saludable posible, es importante que sus dientes y mandíbulas estén correctamente alineados y le den una mordida sana. A muchos padres les sorprende saber que la American Association of Orthodontists recomienda que los niños tengan una revisión de ortodoncia a más tardar a los 7 años.",
			"A esa edad, los dientes de tu hijo se han desarrollado lo suficiente como para detectar pequeños problemas de crecimiento mandibular y dientes emergentes mientras todavía conserva algunos dientes de leche. Una revisión puede mostrar que la mordida de tu hijo está bien y que no necesitará brackets, o podemos identificar un posible problema y recomendar monitorearlo. A partir de ahí, podemos iniciar el tratamiento en el momento adecuado para el niño. En otros casos, podemos recomendar tratamiento temprano.",
		],
		teensHeading: "Atención de ortodoncia para adolescentes en Miami",
		teensParagraphs: [
			"El tratamiento temprano permite que nuestro equipo monitoree el crecimiento y desarrollo de la mandíbula de tu hijo, lo que significa que podemos detectar y comenzar a tratar problemas mientras todavía están en etapas tempranas. Esto puede reducir considerablemente la duración del tratamiento.",
			"Los adolescentes suelen preguntar: “¿Cuánto tiempo estaré con brackets?” No hay una respuesta única, porque cada caso es diferente, igual que cada sonrisa. Siempre haremos todo lo posible para asegurar que tu adolescente tenga su sonrisa más hermosa y duradera. Afortunadamente, los brackets actuales son una gran mejora frente a los que experimentaron generaciones anteriores. Son más pequeños, elegantes y mucho más cómodos que nunca. También vienen en una variedad de estilos, materiales y colores.",
		],
		teenCeramicBefore:
			"Además, los adolescentes tienen más opciones de tratamiento que nunca. Además de los brackets metálicos tradicionales, también pueden elegir brackets ",
		teenCeramicLink: "cerámicos",
		teenCeramicMiddle:
			", que funcionan igual que los brackets tradicionales, pero están diseñados para mezclarse con los dientes de tu adolescente y dar una apariencia más discreta. Además, si tu adolescente quiere una nueva sonrisa sin alambres, puede considerar tratamiento con alineadores transparentes, como Invisalign. ",
		teenInvisalignLink: "Invisalign",
		teenCeramicAfter:
			" endereza los dientes gradualmente mediante una serie de bandejas transparentes y removibles que se ajustan sobre los dientes.",
		adultsHeading: "Ortodoncia para adultos en Miami",
		adultParagraphs: [
			"El tratamiento de ortodoncia no es solo para niños y adolescentes. Más adultos que nunca buscan sonrisas hermosas y duraderas mediante tratamiento de ortodoncia. Hoy, aproximadamente 1 de cada 5 pacientes de ortodoncia es adulto, así que nunca es tarde para obtener esa sonrisa espectacular que siempre has querido.",
			"Muchos adultos eligen recibir tratamiento más adelante en la vida porque entienden lo importante que es mantener su salud oral, quieren sentirse mejor con su apariencia general, o ambas cosas. También vemos a muchos adultos que recibieron tratamiento de ortodoncia en sus primeros años, pero necesitan un retoque para recuperar la sonrisa que alguna vez tuvieron.",
		],
		finalBeforePractice: "En ",
		finalMiddle:
			", reconocemos que niños, adolescentes y adultos tienen diferentes necesidades dentales y requieren distintos niveles de atención y cuidado. Trabajamos cuidadosamente con cada paciente para asegurar que reciba el tratamiento más apropiado en el ambiente más acogedor.",
		finalBeforeConsult: "Programa una ",
		finalConsultLink: "consulta gratuita",
		finalAfterConsult: " con nosotros hoy para comenzar.",
	},
} satisfies Record<Locale, MiamiFamilyOrthodontistLocalization>;

export function MiamiFamilyOrthodontistPage() {
	const getLocalization = useLocalization(miamiFamilyOrthodontistLocalization);

	return (
		<Flex direction="column">
			<HeroStyle2
				heroImageUrl="/Dr-Garcia-Paul.jpg"
				title={getLocalization("heroTitle")}
				backgroundPosition="50% 18%"
				pageHeadingClassName={styles.miamiFamilyHeroHeading}
				pageHeadingWrapperClassName={styles.miamiFamilyHeroHeadingWrapper}
			/>
			<article className={styles.miamiBracesBand}>
				<Box className={styles.miamiBracesInner}>
					<Box className={styles.miamiFamilyFullColumn}>
						<Heading as="h2" className={styles.miamiBracesSectionHeading}>
							{getLocalization("introHeading")}
						</Heading>
					</Box>
					<Grid
						className={`${styles.miamiBracesTwoColumn} ${styles.miamiFamilyDoctorGrid}`}
					>
						<Box className={styles.miamiBracesSmallColumn}>
							<OptimizedImage
								alt="Miami Family Orthodontist"
								className={styles.miamiFamilyDoctorImage}
								height={900}
								src="/Dr-Garcia-Paul.jpg"
								width={900}
							/>
						</Box>
						<Box className={styles.miamiBracesSmallColumn}>
							<Heading as="h3" className={styles.miamiBracesSubheading}>
								{getLocalization("doctorHeading")}
							</Heading>
							{getLocalization("doctorParagraphs").map((paragraph) => (
								<Text as="p" className={styles.miamiBracesCopy} key={paragraph}>
									{paragraph}
								</Text>
							))}
						</Box>
					</Grid>
					<Box
						className={`${styles.miamiFamilyFullColumn} ${styles.miamiFamilyCareColumn}`}
					>
						<Heading as="h3" className={styles.miamiBracesSubheading}>
							{getLocalization("careHeading")}
						</Heading>
						<Text as="p" className={styles.miamiBracesCopy}>
							{getLocalization("careBeforePractice")}
							<Link to="/" className={styles.inlineLink}>
								{getLocalization("practiceLink")}
							</Link>
							{getLocalization("careBeforeTreatment")}
							<Link to="/our-treatments" className={styles.inlineLink}>
								{getLocalization("treatmentLink")}
							</Link>
							{getLocalization("careBeforeInvisalign")}
							<Link to="/invisalign" className={styles.inlineLink}>
								{getLocalization("invisalignLink")}
							</Link>
							{getLocalization("careBeforeAges")}
							<Link
								to="/orthodontics-for-all-ages"
								className={styles.inlineLink}
							>
								{getLocalization("agesLink")}
							</Link>
							{getLocalization("careAfterAges")}
						</Text>
						<Heading as="h4" className={styles.miamiFamilyMinorHeading}>
							{getLocalization("childrenHeading")}
						</Heading>
						{getLocalization("childrenParagraphs").map((paragraph) => (
							<Text as="p" className={styles.miamiBracesCopy} key={paragraph}>
								{paragraph}
							</Text>
						))}
						<Heading as="h4" className={styles.miamiFamilyMinorHeading}>
							{getLocalization("teensHeading")}
						</Heading>
						{getLocalization("teensParagraphs").map((paragraph) => (
							<Text as="p" className={styles.miamiBracesCopy} key={paragraph}>
								{paragraph}
							</Text>
						))}
						<Text as="p" className={styles.miamiBracesCopy}>
							{getLocalization("teenCeramicBefore")}
							<Link to="/clear-braces" className={styles.inlineLink}>
								{getLocalization("teenCeramicLink")}
							</Link>
							{getLocalization("teenCeramicMiddle")}
							<Link to="/miami-invisalign" className={styles.inlineLink}>
								{getLocalization("teenInvisalignLink")}
							</Link>
							{getLocalization("teenCeramicAfter")}
						</Text>
						<Heading as="h4" className={styles.miamiFamilyMinorHeading}>
							{getLocalization("adultsHeading")}
						</Heading>
						{getLocalization("adultParagraphs").map((paragraph) => (
							<Text as="p" className={styles.miamiBracesCopy} key={paragraph}>
								{paragraph}
							</Text>
						))}
						<Text as="p" className={styles.miamiBracesCopy}>
							{getLocalization("finalBeforePractice")}
							<Link to="/" className={styles.inlineLink}>
								{getLocalization("practiceLink")}
							</Link>
							{getLocalization("finalMiddle")}
						</Text>
						<Text as="p" className={styles.miamiBracesCopy}>
							{getLocalization("finalBeforeConsult")}
							<Link to="/free-consult" className={styles.inlineLink}>
								{getLocalization("finalConsultLink")}
							</Link>
							{getLocalization("finalAfterConsult")}
						</Text>
					</Box>
				</Box>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
